import { QueueExecutionArgs } from "@/shared/Executions/models/QueueExecutionArgs";
import { QueueExecutionResponse } from "@/shared/Executions/models/QueueExecutionResponse";
import { AxiosResponse } from "axios";
import { QueueExecutionRequestFactory } from "./QueueExecutionRequestFactory";
import { GetExecutionStatusResponse } from "@/shared/Executions/models/GetExecutionStatusResponse";
import { GetExecutionStatusArgs } from "@/shared/Executions/models/GetExecutionStatusArgs";
import { GetExecutionsArgs } from "@/shared/Executions/models/GetExecutionsArgs";
import { GetExecutionsResponse } from "@/shared/Executions/models/GetExecutionsResponse";
import { InjectableService } from "@/shared/services/InjectableService";
import { GetExecutionOutputPackageArgs } from "@/shared/Executions/models/GetExecutionOutputPackageArgs";
import { IAplosFileApiService } from "@/components/FileManager/services/AplosFileApiService";
import { GetFilesArgs } from "@/components/FileManager/models/GetFilesArgs";
import { AplosFileCategory } from "@/components/FileManager/models/AplosFileCategory";
import { AplosFile } from "@/components/FileManager/models/AplosFile";
import { GetFileDownloadUrlArgs } from "@/components/FileManager/models/GetFileDownloadUrlArgs";
import { GetFileDownloadUrlResponse } from "@/components/FileManager/models/GetFileDownloadUrlResponse";
import { GetExecutionConfigArgs } from "@/shared/Executions/models/GetExecutionConfigArgs";
import { GetExecutionConfigReponse } from "@/shared/Executions/models/GetExecutionConfigReponse";
import { GetExecutionOutputFileArgs, GetExecutionOutputFileType } from "@/shared/Executions/models/GetExecutionOutputFileArgs";
import { CancelExecutionArgs } from "@/shared/Executions/models/CancelExecutionArgs";
import { CancelExecutionReponse } from "@/shared/Executions/models/CancelExecutionReponse";
import { ArchiveExecutionArgs } from "@/shared/Executions/models/ArchiveExecutionArgs";
import { ArchiveExecutionReponse } from "@/shared/Executions/models/ArchiveExecutionReponse";
import { PostProcessingOutput } from "@/models/PostProcessingOutput";
import { GetExecutionPostProcessingOutputArgs } from "@/shared/Executions/models/GetExecutionPostProcessingOutputArgs";
import { PostProcessingOutputItem } from "@/models/PostProcessingOutputItem";
import { getUserPath, HttpMethod, submitApiRequest } from "@/services/aplosApi";


export interface IExecutionApiService {
    queueExecution: (args: QueueExecutionArgs) => Promise<AxiosResponse<QueueExecutionResponse>>;
    getExecutionStatus: (args: GetExecutionStatusArgs) => Promise<AxiosResponse<GetExecutionStatusResponse>>;
    getExecutions: (args: GetExecutionsArgs) => Promise<AxiosResponse<GetExecutionsResponse>>;
    getExecutionOutputPackage: (args: GetExecutionOutputPackageArgs) => Promise<GetFileDownloadUrlResponse>;
    getExecutionOutputReport: (args: GetExecutionOutputPackageArgs) => Promise<GetFileDownloadUrlResponse>;
    getExecutionConfig: (args: GetExecutionConfigArgs) => Promise<AxiosResponse<GetExecutionConfigReponse>>;
    getExecutionOutput(args: GetExecutionOutputFileArgs): Promise<AxiosResponse<AplosFile>>;
    getPostProcessingExecutionOutput<TItem extends PostProcessingOutputItem>(args: GetExecutionPostProcessingOutputArgs): Promise<AxiosResponse<PostProcessingOutput<TItem>>>;
    cancelExecution: (args: CancelExecutionArgs) => Promise<AxiosResponse<CancelExecutionReponse>>;
    archiveExecution: (args: ArchiveExecutionArgs) => Promise<AxiosResponse<ArchiveExecutionReponse>>;
}


export class ExecutionApiService extends InjectableService implements IExecutionApiService {

    static readonly injectableKey = 'IExecutionApiService';

    get injectableKey() {
        return (this.constructor as typeof ExecutionApiService).injectableKey;
    }

    constructor(private readonly _aplosFileApiService: IAplosFileApiService) {
        super()
        if (_aplosFileApiService == null) throw new Error("_aplosFileApiService is required!");
    }

    public async queueExecution(args: QueueExecutionArgs): Promise<AxiosResponse<QueueExecutionResponse>> {

        if (args == null) throw Error("valid args are required");

        let payload = QueueExecutionRequestFactory.createQueueExecutionRequest(args);

        return await submitApiRequest(
            HttpMethod.POST,
            `${getUserPath(args.tenantId, args.userId)}/analysis/queue`,
            payload
        );

    }

    public async getExecutionStatus(args: GetExecutionStatusArgs): Promise<AxiosResponse<GetExecutionStatusResponse>> {

        if (args == null) throw Error("valid args are required");

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/status`
        );

    }

    public async getExecutions(args: GetExecutionsArgs): Promise<AxiosResponse<GetExecutionsResponse>> {

        if (args == null) throw Error("valid args are required");

        const params = new URLSearchParams()
        if (args.executionWorkflowType != null) params.set('workflow_type', String(args.executionWorkflowType));
        if (args.startDate != null) params.set('start_date', String(args.startDate));
        if (args.endDate != null) params.set('end_date', String(args.endDate));
        if (args.limit != null) params.set('limit', String(args.limit));
        if (args.startKey != null) params.set('start_key', String(args.startKey));
        if (args.status != null) params.set('status', String(args.status));
        if (args.viewType != null) params.set('view', String(args.viewType));
        if (args.lifecycleState != null) params.set('lifecycle_state', String(args.lifecycleState));
        if (args.folderId != null) params.set('folder_id', String(args.folderId));

        const queryString = params.toString() ? `?${params.toString()}` : '';

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/executions${queryString}`
        );

    }

    public async getExecutionOutputPackage(args: GetExecutionOutputPackageArgs): Promise<GetFileDownloadUrlResponse> {

        if (args == null) throw Error("valid args are required");

        const getFilesArgs = new GetFilesArgs({ tenantId: args.tenantId, userId: args.userId, category: AplosFileCategory.Execution });
        getFilesArgs.executionId = args.executionId;

        const getExecutionOutputFileResponse = await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/outputs/package`
        );

        const outputFile = getExecutionOutputFileResponse.data as AplosFile;
        if (!outputFile?.id) throw Error("Invalid or unexpected getExecutionOutput pagckage response.");

        const getFileDownloadUrlArgs = new GetFileDownloadUrlArgs({ tenantId: args.tenantId, userId: args.userId, fileId: outputFile.id as string });

        const getFileDownloadUrlResponse = await this._aplosFileApiService.getFileDownloadUrl(getFileDownloadUrlArgs);
        return getFileDownloadUrlResponse.data;

    }

    public async getExecutionOutputReport(args: GetExecutionOutputPackageArgs): Promise<GetFileDownloadUrlResponse> {

        if (args == null) throw Error("valid args are required");

        const getExecutionOutputFileResponse = await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/outputs/report`
        );

        const outputFile = getExecutionOutputFileResponse.data as AplosFile;
        if (!outputFile?.id) throw Error("Invalid or unexpected getExecutionOutput report response.");

        const getFileDownloadUrlArgs = new GetFileDownloadUrlArgs({ tenantId: args.tenantId, userId: args.userId, fileId: outputFile.id as string });

        const getFileDownloadUrlResponse = await this._aplosFileApiService.getFileDownloadUrl(getFileDownloadUrlArgs);
        return getFileDownloadUrlResponse.data;

    }

    public async getExecutionConfig(args: GetExecutionConfigArgs): Promise<AxiosResponse<GetExecutionConfigReponse>> {

        if (args == null) throw Error("valid args are required");

        return await submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/config`
        );

    }

    public getExecutionOutput(args: GetExecutionOutputFileArgs): Promise<AxiosResponse<AplosFile>> {

        let outputPath: string | null = null;
        switch (args.fileType) {
            case GetExecutionOutputFileType.PkResults:
                outputPath = 'pk-results.csv';
                break;
            case GetExecutionOutputFileType.PkResultsAll:
                outputPath = 'pk-results-all.csv';
                break;
            case GetExecutionOutputFileType.PkResultsCustomCalcs:
                outputPath = 'pk-results-custom-calcs.csv';
                break;
            default:
                throw Error(`Unsupported GetExecutionOutputFileType encountered: ${args.fileType}`);
        }

        return submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/outputs/${outputPath}`
        );

    }

    public getPostProcessingExecutionOutput<TItem extends PostProcessingOutputItem>(args: GetExecutionPostProcessingOutputArgs): Promise<AxiosResponse<PostProcessingOutput<TItem>>> {

        return submitApiRequest(
            HttpMethod.GET,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/outputs/type/${args.outputType}`
        );

    }

    public async cancelExecution(args: CancelExecutionArgs): Promise<AxiosResponse<CancelExecutionReponse>> {

        if (args == null) throw Error("valid args are required");

        let payload = { reason: "User initiated cancel." }

        return submitApiRequest(
            HttpMethod.POST,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/cancel`,
            payload
        );

    }

    public async archiveExecution(args: ArchiveExecutionArgs): Promise<AxiosResponse<ArchiveExecutionReponse>> {

        if (args == null) throw Error("valid args are required");

        let payload = { reason: "User initiated archive." }

        return submitApiRequest(
            HttpMethod.POST,
            `${getUserPath(args.tenantId, args.userId)}/executions/${args.executionId}/archive`,
            payload
        );

    }

}